import React from 'react'
import Styles from './styles'
import { UncontrolledTooltip } from 'reactstrap'
import { FormattedMessage } from 'react-intl'
import createLinks from '~/helpers/createLinks'
import { SHORT_ID_LENGTH } from '~/constants/byoGG'
import { ReactComponent as AttachmentIcon } from '../../.././../../assets/img/svg/attachment.svg'
import { ReactComponent as GoogleBusinessProfile } from '../../.././../../assets/img/svg/google-business.svg'
import { ReactComponent as QRCodeIcon } from '../../.././../../assets/img/svg/qrcode.svg'
import { ReactComponent as WhatsappIcon } from '../../.././../../assets/img/svg/whatsapp.svg'
import { toast } from 'react-toastify'
import { Link } from 'react-router-dom/cjs/react-router-dom.min'
import LocationProgressBar from '../ProgressBar'
import _ from 'lodash'
import getLocationImage from '~/helpers/getLocationImage'
import Skeleton from '~/components/Skeleton'

const LocationCard = ({ userIsClient = true, location, isLoading = false }) => {
  const byoGGURL = createLinks.byoGG(location)
  const whatsappURL = createLinks.whatsapp(location)

  const copyToClipboard = text => {
    navigator.clipboard.writeText(text)
  }
  if (isLoading) {
    return <LoadingLocationCard userIsClient={userIsClient} />
  }

  const profilePhoto = getLocationImage.profile({
    location: location,
  })?.url

  const coverPhoto = getLocationImage.cover({
    location: location,
  })?.url
  return (
    <Styles.Card>
      <Styles.CardContent>
        <Styles.Banner src={coverPhoto} />
        <Styles.ProfileInfoWrapper>
          <Styles.ProfileImage src={profilePhoto} />
          <custom-content>
            <tags-and-progress>
              <tags>
                <Styles.Badge color={location.status}>
                  <FormattedMessage id={_.lowerCase(location.status)} />
                </Styles.Badge>
                {location.code && (
                  <>
                    <UncontrolledTooltip
                      placement="top"
                      target={`location-code-${location.code.replaceAll(
                        ' ',
                        ''
                      )}`}
                    >
                      Clique para copiar: {location.code}
                    </UncontrolledTooltip>
                    <Styles.Badge
                      style={{ cursor: 'pointer' }}
                      onClick={() => copyToClipboard(location.code)}
                      id={`location-code-${location.code.replaceAll(' ', '')}`}
                    >
                      <span>{location.code}</span>
                    </Styles.Badge>
                  </>
                )}
              </tags>
              <LocationProgressBar progress={location.score} />
            </tags-and-progress>
            <title-and-address>
              <h3>{location.name}</h3>
              <div>
                <address>
                  <span>
                    {(location.address || []).join(', ')}
                    {location?.neighborhood && `, ${location.neighborhood}`}
                  </span>
                  <span>
                    - {location?.city}{' '}
                    {location?.city && location?.state && '/'} {location?.state}
                  </span>
                </address>
              </div>
            </title-and-address>
          </custom-content>
        </Styles.ProfileInfoWrapper>
      </Styles.CardContent>
      <Styles.CardFooter>
        <left-content>
          {byoGGURL && (
            <>
              <UncontrolledTooltip
                placement="top"
                target={`attachment-link-${location.id.slice(
                  -SHORT_ID_LENGTH
                )}`}
              >
                <FormattedMessage id="Open Byo.gg profile" />
              </UncontrolledTooltip>
              <UncontrolledTooltip
                placement="top"
                target={`qrcode-link-${location.id.slice(-SHORT_ID_LENGTH)}`}
              >
                <FormattedMessage id="Open review QR Code" />
              </UncontrolledTooltip>
            </>
          )}
          <UncontrolledTooltip
            placement="top"
            target={`google-link-${location.id.slice(-SHORT_ID_LENGTH)}`}
          >
            <FormattedMessage id="Google Business profile review URL" />
          </UncontrolledTooltip>
          {whatsappURL && (
            <UncontrolledTooltip
              placement="top"
              target={`whatsapp-link-${location.id.slice(-SHORT_ID_LENGTH)}`}
            >
              <FormattedMessage id="Copy Whatsapp URL" />
            </UncontrolledTooltip>
          )}

          {byoGGURL && (
            <a
              id={`attachment-link-${location.id.slice(-SHORT_ID_LENGTH)}`}
              href={byoGGURL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <AttachmentIcon width="20" />
            </a>
          )}
          <a
            id={`google-link-${location.id.slice(-SHORT_ID_LENGTH)}`}
            href={location.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <GoogleBusinessProfile width="18" />
          </a>
          {byoGGURL && (
            <a
              id={`qrcode-link-${location.id.slice(-SHORT_ID_LENGTH)}`}
              href={`${byoGGURL}/review-google`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <QRCodeIcon width="20" />
            </a>
          )}
          {whatsappURL && (
            <button
              id={`whatsapp-link-${location.id.slice(-SHORT_ID_LENGTH)}`}
              onClick={() => {
                navigator.clipboard.writeText(whatsappURL)
                toast.success(<FormattedMessage id="Copied to clipboard" />)
              }}
            >
              <WhatsappIcon width="20" />
            </button>
          )}
        </left-content>
        {!userIsClient && (
          <Link to={`/${location.id}/edit`} className="right-content">
            <button>
              <span>
                <FormattedMessage id="Edit" />
              </span>
            </button>
          </Link>
        )}
      </Styles.CardFooter>
    </Styles.Card>
  )
}

const LoadingLocationCard = ({ userIsClient = true }) => {
  const [cardWidth, setCardWidth] = React.useState(0)
  const handleCardWidth = React.useCallback(
    width => {
      setCardWidth(width)
    },
    [setCardWidth]
  )
  return (
    <Styles.Card>
      <Styles.CardContent ref={e => e && handleCardWidth(e.offsetWidth)}>
        <Skeleton
          width={cardWidth - 18}
          height={'126px'}
          style={{
            flex: '1 0 1',
          }}
        />
        <Styles.ProfileInfoWrapper>
          <Skeleton width={88} height={88} />
          <content>
            <tags-and-progress>
              <tags>
                <Skeleton width={59} height={21} />

                <Skeleton width={59} height={21} />
              </tags>
              <Skeleton width={122} height={20} />
            </tags-and-progress>
            <title-and-address>
              <Skeleton width={cardWidth - 48} height={22} />
              <div>
                <address>
                  <Skeleton
                    width={cardWidth - 48}
                    height={20}
                    count={2}
                    style={{
                      opacity: 0.5,
                    }}
                  />
                </address>
              </div>
            </title-and-address>
          </content>
        </Styles.ProfileInfoWrapper>
      </Styles.CardContent>
      <Styles.CardFooter>
        <left-content>
          <Skeleton width={20} height={20} />
          <Skeleton width={20} height={20} />
          <Skeleton width={20} height={20} />
          <Skeleton width={20} height={20} />
        </left-content>
        {!userIsClient && (
          <div style={{ flex: '1 0 0', marginTop: '-4px' }}>
            <Skeleton
              height={48}
              style={{
                flex: '1 0 0',
              }}
            ></Skeleton>
          </div>
        )}
      </Styles.CardFooter>
    </Styles.Card>
  )
}

export default LocationCard
